<template>
  <div class="create-work-form" :class="{toBack: sendToBack}"
       @dragover.prevent="handleDragOver"
       @dragleave="handleDragLeave"
       @drop.prevent="handleDrop"
  >
    <div class="header">
      <h2>{{ title }}</h2>
      <button class="close-btn" @click="close">×</button>
    </div>
    <div class="content">
      <div class="form-group flex-grow">
        <label for="impressum">Impressum</label>
        <input
            type="text"
            class="mb-2"
            id="impressum"
            v-model="impressum"
            placeholder="Impressum"
        />
        <label for="tag">Caption</label>
        <textarea
            type="text"
            rows="1"
            id="tag"
            v-model="tag"
            placeholder="Caption"
        />
        <label for="keywords">Keywords</label>
        <input
            type="text"
            class="mb-2"
            id="keywords"
            v-model="keywords"
            placeholder="Keywords"
        />
        <small
            class="mb-2"><u>Click here</u> to learn about the upload qualities.</small>

        <div class="content-images">
          <label for="visuals">Visuals</label>
          <!-- Flex container to wrap the draggable items and the placeholder -->
          <div class="flex-wrap-container">

            <div class="empty-visuals" v-if="contentImages.length === 0">Drop files here</div>
            <draggable
                class="content-image-horizontal"
                v-model="contentImages"
                item-key="image"
            >
              <template #item="{ element, index }">
                <Uploader
                    :key="getUniqueKey(index)"
                    :ref="setUploaderRef(index)"
                    class="content-image upload-popover"
                    :num="index"
                    :existingImage="element.image"
                    :existingFile="element.file"
                    @progressChanged="progressChanged"
                    @makeCover="makeCover"
                    @imageSelected="contentContentSelected"
                    @multipleSelected="multipleContentSelected"
                    @menuToggled="menuToggled"
                    @uploadTypeChanged="uploadTypeChanged"
                />
              </template>
            </draggable>

          </div>

        </div>
      </div>
    </div>
    <div class="footer">
      <div class="progress-bar" v-if="isCompressing">
        <div class="progress" :style="{ width: progress + '%' }"></div>
      </div>
      <div class="add-more">
        <Popover :width="150" :isOpen="isPopoverOpen" :disabled="isPopoverDisabled" @update:isOpen="updatePopoverState">
          <template #trigger>
            <div class="flex items-center gap-3">
              <RoundedIcon src="/icons/plus_black.png"></RoundedIcon>
              Add more
            </div>
          </template>
          <ul class="popover-menu">
            <li v-for="(item, index) in showingMenuItems" :key="index" @click="handleItemClick(item)">
              {{ item }}
            </li>
          </ul>
        </Popover>
        <div class="placeholder-wrapper">
          <UploaderPlaceholder
              ref="placeholderUploader"
              class="content-image upload-popover"
              :num="contentImages.length"
              :existingImage="''"
              @progressChanged="progressChanged"
              @makeCover="makeCover"
              @imageSelected="contentContentSelected"
              @multipleSelected="multipleContentSelected"
              @menuToggled="menuToggled"
              @uploadTypeChanged="uploadTypeChanged"
          />
        </div>
      </div>
      <div class="button-wrapper">
        <button class="create-btn" :disabled="!canCreate" @click="submitWork">
          {{ isEditMode ? 'Update' : 'Create' }}
        </button>
      </div>

      <div class="drop-here" v-if="false"
           :style="{ pointerEvents: isDragging ? 'none' : 'auto' }">
        <span>Drop here</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {watch, defineProps, defineEmits, ref, computed, nextTick} from 'vue';
import draggable from 'vuedraggable';

import Uploader from '~/components/works/profile/Uploader.vue';
import {useUserStore} from '~/composables/useUserStore';
import {useWorkStore} from '~/composables/useWorkStore';
import * as pkg from 'vue-toastification';
import UploaderPlaceholder from "~/components/works/profile/UploaderPlaceholder.vue";
import Popover from "~/components/Popover.vue";
import RoundedIcon from "~/components/RoundedIcon.vue";

const {useToast} = pkg;

const toast = useToast();
const {newWork, patchWork} = useWorkStore();
const {user, visitingUser} = useUserStore();
const showUploaderPlaceholder = computed(() => contentImages.value.length < 100);

const props = defineProps({
  work: {
    type: Object,
    default: () => ({
      _id: '',
      title: '',
      tag: '',
      visuals: '',
      category: 'photography',
      coverImage: '',
      contentImages: [],
    }),
  },
  editing: {
    type: Boolean,
    default: false
  },
  isAdmin: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);
const isEditMode = computed(() => !!props.work._id);
const titleType = computed(() => contentImages.value.length > 1 ? 'work' : 'work');
const title = computed(() => props.editing ? `Edit ${titleType.value}` : `Create ${titleType.value}`);

const showingMenuItems = computed(() => {
  return ['Upload item', 'Embed video'];
});
const isPopoverOpen = ref(false);
const isPopoverDisabled = ref(false);
const placeholderUploader = ref();
const impressum = ref(props.work.title);
const visuals = ref(props.work.visuals);
const category = ref(props.work.category);
const tag = ref(props.work.tag);
const keywords = ref(props.work.keywords);
const layoutContentImages = computed({
  get() {
    return [...contentImages.value, {}];  // Getter: Return the value
  },
  set(newValue) {
    // Setter: Modify the value of `contentImages` based on `newValue`
    contentImages.value = [...newValue];  // Update `contentImages` with the new value
  }
});

const isCompressing = ref(false); // Flag to check if compression is running
const progress = ref(0); // Progress percentage
const isDragging = ref(false); // Flag to track drag state

const handleDragOver = (event: any) => {
  isDragging.value = true; // Show "Drop here" message when a file is being dragged over
  event.preventDefault();
  event.stopPropagation();
};

const handleDragLeave = () => {
  isDragging.value = false; // Hide "Drop here" message when dragging leaves
};
const updatePopoverState = (newState: any) => {
  isPopoverOpen.value = newState;
};

const progressChanged = (change: boolean) => {
  isUploading.value = change;
};

const contentImages = ref<any[]>(props.work.contentImages.map((img: any) => ({
  image: img,
  file: null
})));
const coverImage = ref(props.work.coverImage);
const coverImageFile = ref<File | any>(null);
// Track initial state to detect changes
const initialWork = JSON.stringify({
  impressum: props.work.title,
  visuals: props.work.visuals,
  tag: props.work.tag,
  keywords: props.work.keywords,
  contentImages: props.work.contentImages,
});

// Detect if the user has made changes
const isChanged = ref(false);
const refresh = ref(-1);
const isUploading = ref(false);
const canCreate = computed(() => {
  return contentImages.value.filter(element => element.image === '').length === 0 && isChanged.value && refresh.value === -1;
});

const canClose = computed(() => {
  return !isCompressing.value && !isUploading.value;
});


const handleItemClick = (index: any) => {

  placeholderUploader.value.handleItemClick(index);
  isPopoverOpen.value = false;
};
// Watch for changes to the form data
watch(
    [impressum, tag, visuals, contentImages, keywords],
    detectChanges,
    {immediate: true, deep: true}
);
watch(refresh, () => {
  if (refresh.value !== -1) {
    setTimeout(() => {
      refresh.value = -1;
    }, 300);
  }
});

function detectChanges() {
  const currentWork = JSON.stringify({
    impressum: impressum.value,
    visuals: visuals.value,
    tag: tag.value,
    keywords: keywords.value,
    contentImages: contentImages.value.map(imageObj => imageObj.image),
  });

  isChanged.value = initialWork !== currentWork;
  console.log('change', isChanged.value, initialWork, currentWork);
}

const uploaderRefs = ref<any[]>([]);

const setUploaderRef = (index: number) => (el: any) => {
  if (el) {
    uploaderRefs.value[index] = el;
  } else {
    delete uploaderRefs.value[index];
  }
};

const onMove = (event: any) => {
  const {relatedContext, draggedContext} = event;
  if (relatedContext.component !== draggedContext.component) {
    return false;
  }
  return true;
};

const handleDrop = async (event: DragEvent) => {

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = false; // Hide "Drop here" after drop
  const files = event.dataTransfer?.files;
  if (files) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSizeInBytes = 50 * 1024 * 1024;
      const maxSizeInBytesVideo = 100 * 1024 * 1024;
      const fileType = file.type.split('/')[0]; // Get the file type (e.g., "image", "video")

      let fileForSave = file;
      // Only apply the size check for image files
      if (fileType === 'image') {
        if (file.size > maxSizeInBytes) {
          toast.error('File size exceeds 50 MB. Please select a smaller image.');
          return;
        }
        fileForSave = await compressImage(file);

      } else if (fileType === 'video') {
        if (file.size > maxSizeInBytesVideo) {
          toast.error('File size exceeds 100 MB. Please select a smaller video.');
          return;
        }
        fileForSave = file;
      }


      const reader = new FileReader();
      reader.onload = (e: any) => {
        const imageUrl = e.target.result;

        contentImages.value.push({image: imageUrl, file: fileForSave});
      };
      reader.readAsDataURL(fileForSave);
    }
  }
};

const close = () => {
  console.log('canClose', canClose.value);
  if (canClose.value) {
    emit('close');
  } else {
    toast.error('Please wait for the upload to finish.');
  }
};

const getUniqueKey = (index: number) => {
  return `${index}-uploader`;
};

const addContentImage = () => {
  placeholderUploader.value.openFileDialog();
};

import imageCompression from 'browser-image-compression';

// Function to compress image and return a compressed File
const compressImage = async (imageFile: File): Promise<File> => {
  const options = {
    maxSizeMB: 1, // Max size in MB
    maxWidthOrHeight: 1920, // Max dimension
    useWebWorker: true, // Use web worker for better performance
    initialQuality: 0.8, // Initial quality setting
    onProgress: (p) => {
      progress.value = Math.round(p); // Update progress
    },
  };
  try {
    isCompressing.value = true; // Set flag to true when compression starts
    // Compress the image file
    const compressedBlob = await imageCompression(imageFile, options);

    // Create a new File object from the compressed Blob, preserving original name and type
    const compressedFile = new File(
        [compressedBlob],
        imageFile.name, // Preserve the original filename
        {type: imageFile.type} // Preserve the original file type (e.g., "image/jpeg")
    );
    isCompressing.value = false; // Set flag to false after compression
    progress.value = 100; // Set progress to 100% once done

    return compressedFile; // Return the compressed file
  } catch (error) {
    console.error('Error compressing image:', error);
    isCompressing.value = false;
    return imageFile; // Return original file if compression fails
  }
};
const multipleContentSelected = async (num: number, files: any[]) => {
  if (files) {
    for (let i = 0; i < files.length; i++) {
      const file = await compressImage(files[i]);

      const maxSizeInBytes = 50 * 1024 * 1024;
      const maxSizeInBytesVideo = 100 * 1024 * 1024;
      const fileType = file.type.split('/')[0]; // Get the file type (e.g., "image", "video")

      let fileForSave = file;
      // Only apply the size check for image files
      if (fileType === 'image') {
        if (file.size > maxSizeInBytes) {
          toast.error('File size exceeds 50 MB. Please select a smaller image.');
          return;
        }
        fileForSave = await compressImage(file);

      } else if (fileType === 'video') {
        if (file.size > maxSizeInBytesVideo) {
          toast.error('File size exceeds 100 MB. Please select a smaller video.');
          return;
        }
        fileForSave = file;
      }


      const reader = new FileReader();
      reader.onload = (e: any) => {
        const imageUrl = e.target.result;
        contentImages.value.push({image: imageUrl, file: fileForSave});
      };
      reader.readAsDataURL(fileForSave);
    }
  }
}
const makeCover = (index: any) => {
  if (index > -1 && index < contentImages.value.length) {
    const selectedImage = contentImages.value.splice(index, 1)[0]; // Remove the item at the given index
    contentImages.value.unshift(selectedImage); // Add the removed item to the beginning of the array
    detectChanges(); // Trigger a change detection
  }
};
const sendToBack = ref(false);
const uploadTypeChanged = (type: any) => {
  sendToBack.value = !type;
};
const menuToggled = (num: any) => {
  const contentUploaderInstances = uploaderRefs.value.filter((uploader, index) => uploader !== null && index !== num);
  contentUploaderInstances.map(upl => {
    upl.updatePopoverState(false);
  })
};

const contentContentSelected = (type: string, content: any, num: number, file: any) => {
  if (type === 'image' || type === 'video') {
    if (typeof contentImages.value[num] !== 'undefined') {
      if (contentImages.value[num].image !== content) {
        contentImages.value[num] = {image: content, file};
      }
    } else {
      contentImages.value.push({image: content, file});
    }
  } else if (type === 'remove') {
    contentImages.value.splice(num, 1);
  }
  refresh.value = num;
};

const coverContentSelected = (type: string, content: any, num: number, file: File) => {
  if (type === 'image' || type === 'video') {
    coverImage.value = content;
    coverImageFile.value = file;
  } else if (type === 'remove') {
    coverImage.value = '';
    coverImageFile.value = null;
  }
};

const submitWork = async () => {
  console.log('uploaders:', uploaderRefs.value);
  let contentUploaderInstances;
  if (contentImages.value.length > 0) {
    try {
      contentUploaderInstances = uploaderRefs.value.filter(uploader => uploader !== null);
    } catch (e) {
      console.log('e', e);
      contentUploaderInstances = [uploaderRefs.value];
    }

    try {
      isUploading.value = true;
      const contentImageUrls = await Promise.all(contentUploaderInstances.map(uploader => uploader.uploadFile()));

      console.log('Content Image URLs:', contentImageUrls);

      const workData = {
        user: props.isAdmin ? visitingUser.value._id : user.value._id,
        title: impressum.value,
        tag: tag.value,
        keywords: keywords.value,
        contentImages: contentImageUrls,
        category: category.value,
        visuals: visuals.value,
        index: isEditMode.value ? props.work.index : -1,
        description: '',
      };

      const response = isEditMode.value ? await patchWork(workData, props.work._id) : await newWork(workData);
      console.log('response', response);
      isUploading.value = false;
      emit('close', response);
    } catch (error) {
      console.error('Error creating work:', error);
      toast.error('Failed to create work. Please try again.');
    }
  } else {
    toast.error('Minimum 1 visual required to proceed.');
  }
};
</script>


<style scoped>
.toBack {

}

.create-work-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: #000000;
  position: relative;
}

.drop-here span {
  border: 1px dashed #000000;
  padding: 10px 40px;
  border-radius: 10px;
  pointer-events: auto; /* Only allow pointer events for the span text */

}

.empty-visuals {

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, .9);
  border: 1px dashed #000000;
  border-radius: 10px;
  z-index: 100;
  pointer-events: none; /* Initially disable pointer events */
  margin: 0;
  font-size: 18px;
  color: #555;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
}

.drop-here {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, .9);
  border: 1px dashed #000000;
  border-radius: 10px;
  z-index: 100;
  pointer-events: none; /* Initially disable pointer events */
  margin: 10px;
  font-size: 18px;
  color: #555;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Flex container that wraps both draggable items and the placeholder */
.flex-wrap-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  overflow-y: scroll;
  max-height: 250px;
  min-height: 100px;
  position: relative;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.header h2 {
  font-size: 28px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
}

.content {
  display: flex;
  gap: 20px;
  padding: 0 20px;
}

.form-group {
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
}

.form-group textarea,
.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
}

.form-group small {
  display: block;
  margin-top: 0;
  color: #b8b8b8;
  font-size: 8px;
  cursor: pointer;
}

.content-images {
  max-width: 100%;
  overflow: visible;
  padding: 0;
}

.content-images label {
  font-size: 14px;
  margin-bottom: 10px;
  display: block;
}

.placeholder-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.content-image-horizontal {
  padding: 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  border-radius: 10px;
  overflow: visible;
  position: relative; /* Ensure it is relative for the absolute positioned popover */
}

.content-image-horizontal img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 10px;
}

.footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.footer .add-more {
  display: flex;
  justify-content: flex-start;
  padding: 10px 20px;
  align-items: center;
  font-size: 14px;
  gap: 20px;
  border-top: 1px solid #b8b8b8;
  border-bottom: 1px solid #b8b8b8;
  cursor: pointer;
}

.footer .button-wrapper {
  background: #EDEDED;
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  display: flex;
  justify-content: center;
}

.add-btn {
  background-color: white;
  border: 1px solid #b8b8b8;
  padding: 0px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 12px;
  color: black;
}

.create-btn {
  background-color: #ccc;
  border: none;
  min-width: 140px;
  padding: 5px 0;
  border-radius: 25px;
  cursor: not-allowed;
  color: #b8b8b8;
}

.create-btn:enabled {
  background-color: #E3FF00;
  cursor: pointer;
  color: black;
}

.add {
  flex: 0;
  width: 100px;
  min-width: 100px;
  height: 100px;
  border: 1px solid #b8b8b8;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

.add img {
  width: 40px;
  height: 40px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #f3f3f3;
}

.progress {
  height: 100%;
  background-color: #00dc82;
}
</style>